import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import { useCart } from "@/contexts/CartContext";
import { ArrowUpRight, ShoppingBag, Loader2 } from "lucide-react";

const PLACEHOLDER_IMG =
  "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80";

const currencySymbol = (code) => (code === "EUR" ? "€" : code === "USD" ? "$" : code === "GBP" ? "£" : code + " ");
const formatPrice = (amount, code) => {
  const sym = currencySymbol(code);
  const num = Number(amount).toFixed(2);
  return code === "EUR" || code === "GBP" ? `${num} ${sym}` : `${sym}${num}`;
};

export default function ProductCard({ product, index = 0 }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [busy, setBusy] = useState(false);

  const img = product.featuredImage?.url || product.images?.[0]?.url || PLACEHOLDER_IMG;
  const firstVariant = product.variants?.find((v) => v.availableForSale) || product.variants?.[0];
  const isAvailable = product.availableForSale && firstVariant?.availableForSale;
  const itemNum = String(index + 1).padStart(2, "0");
  const hasOptions = (product.options || []).some(
    (o) => o.name !== "Title" && (o.values?.length || 0) > 1
  );

  const goToDetail = () => navigate(`/product/${product.handle}`);

  const onQuickAdd = (e) => {
    e.stopPropagation();
    if (!isAvailable || !firstVariant) return;
    // If product has multiple options (size/color), force user to PDP to choose
    if (hasOptions) {
      goToDetail();
      return;
    }
    setBusy(true);
    addItem({
      variantId: firstVariant.id,
      productHandle: product.handle,
      title: product.title,
      variantTitle: firstVariant.title,
      price: Number(firstVariant.price?.amount || product.price || 0),
      currency: firstVariant.price?.currencyCode || product.currency || "EUR",
      image: img,
      quantity: 1,
    });
    toast.success(t.pdp.addedToCart);
    setTimeout(() => setBusy(false), 400);
  };

  return (
    <article
      data-testid={`product-card-${product.handle}`}
      className="group kh-card kh-belt-frame relative flex flex-col overflow-hidden cursor-pointer"
      onClick={goToDetail}
    >
      {/* Item number */}
      <div className="absolute top-3 left-3 z-20 flex items-baseline gap-1">
        <span className="font-serif-luxury italic text-[#B5651D] text-xs">No.</span>
        <span className="font-display text-base text-[#D4AF37]">{itemNum}</span>
      </div>

      {/* Image */}
      <div className="relative aspect-[3/4] overflow-hidden bg-[#0a0a0f]">
        <img
          src={img}
          alt={product.featuredImage?.altText || product.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {product.productType && (
          <span className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/75 backdrop-blur-sm border border-[#D4AF37]/35 font-body text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">
            {product.productType}
          </span>
        )}

        {!isAvailable && (
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm flex items-center justify-center">
            <span className="font-display text-xl tracking-[0.25em] uppercase text-white border border-white px-5 py-1.5">
              {t.products.soldOut}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col gap-3 flex-1 bg-[#11111a]">
        <h3 className="font-display text-xl sm:text-2xl uppercase text-white leading-tight tracking-wide line-clamp-2 min-h-[3rem]">
          {product.title}
        </h3>
        <div className="flex items-baseline justify-between pt-2 border-t border-[#1f1f29]">
          <div className="flex items-baseline gap-2">
            <span className="font-body text-[10px] uppercase tracking-[0.25em] text-white/40">
              {t.products.from}
            </span>
            <span
              data-testid={`product-price-${product.handle}`}
              className="font-display text-xl text-[#D4AF37]"
            >
              {formatPrice(product.price, product.currency)}
            </span>
          </div>
          <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
        </div>

        {/* Two-tier action: View + Quick Add */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            data-testid={`product-view-${product.handle}`}
            onClick={(e) => { e.stopPropagation(); goToDetail(); }}
            className="py-3 bg-transparent border border-[#1f1f29] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors font-display text-xs tracking-[0.18em] uppercase"
          >
            {t.products.view}
          </button>
          <button
            data-testid={`product-add-${product.handle}`}
            onClick={onQuickAdd}
            disabled={!isAvailable || busy}
            className="flex items-center justify-center gap-2 py-3 bg-[#D4AF37] text-black hover:bg-[#A93043] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-display text-xs tracking-[0.18em] uppercase"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ShoppingBag className="w-3.5 h-3.5" />
                {hasOptions ? t.products.choose : t.products.addShort}
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}
